/**
 * Country-aware formatters for currency / date / phone.
 * Driven by the tenant's country preset loaded from GET /api/my/country-preset.
 *
 * Keep logic minimal and framework-agnostic so individual components can import
 * these helpers directly without touching the React context.
 */

const DEFAULT_PRESET = {
  country_code: "VE",
  currency_code: "VES",
  currency_symbol: "Bs.",
  locale: "es-VE",
  timezone: "America/Caracas",
  utc_offset: -4,
  date_format: "DD/MM/YYYY",
  calling_code: "+58",
};

export function getCurrencyDecimals(currency) {
  // ISO 4217 common exceptions — most currencies use 2 decimals.
  const c = (currency || "").toUpperCase();
  if (c === "CLP" || c === "JPY" || c === "KRW" || c === "VND") return 0;
  return 2;
}

/**
 * Format a monetary value using the tenant's locale + currency.
 * `override` lets callers pin currency (e.g. "USD") regardless of preset.
 */
export function fmtCurrency(value, preset, override) {
  const p = preset || DEFAULT_PRESET;
  const currency = override || p.currency_code || "USD";
  const locale = p.locale || "en-US";
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: getCurrencyDecimals(currency),
      maximumFractionDigits: getCurrencyDecimals(currency),
    }).format(n);
  } catch (_e) {
    // Currency not recognised by Intl — fall back to symbol + number.
    return `${p.currency_symbol || ""} ${n.toFixed(getCurrencyDecimals(currency))}`.trim();
  }
}

/** Shorthand for formatting a plain number per locale (no currency). */
export function fmtNumber(value, preset, opts = {}) {
  const p = preset || DEFAULT_PRESET;
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat(p.locale || "en-US", opts).format(n);
  } catch (_e) {
    return String(n);
  }
}

/**
 * Format an ISO datetime/date string per the tenant's date_format + timezone.
 * `mode` can be "date" | "datetime" | "time".
 */
export function fmtDate(iso, preset, mode = "datetime") {
  if (!iso) return "";
  const p = preset || DEFAULT_PRESET;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const opts = { timeZone: p.timezone || undefined };
  if (mode === "date" || mode === "datetime") {
    opts.year = "numeric"; opts.month = "2-digit"; opts.day = "2-digit";
  }
  if (mode === "time" || mode === "datetime") {
    opts.hour = "2-digit"; opts.minute = "2-digit"; opts.hour12 = false;
  }
  try {
    return new Intl.DateTimeFormat(p.locale || "en-US", opts).format(d);
  } catch (_e) {
    return d.toISOString().slice(0, 16).replace("T", " ");
  }
}

/**
 * Pretty-print a phone number with the country's calling-code prefix.
 * Does not validate — simply normalises leading country code.
 */
export function fmtPhone(raw, preset) {
  if (!raw) return "";
  const p = preset || DEFAULT_PRESET;
  const digits = String(raw).replace(/[^0-9]/g, "");
  if (!digits) return "";
  const cc = (p.calling_code || "").replace("+", "");
  let local = digits;
  if (cc && digits.startsWith(cc)) local = digits.slice(cc.length);
  // Group local as XXX-XXX-XXXX when long enough
  const grouped = local.length >= 7
    ? local.replace(/^(\d{3})(\d{3})(\d+)$/, "$1-$2-$3")
    : local;
  return `+${cc || ""} ${grouped}`.trim();
}
